import React from 'react'
import DataRepo from '@api/datasource/data'
import QueryKeys from '@constants/queryKeys'
import {
  CreateStatusClientSchema,
  CreateStatusClientType,
  StatusClientType,
} from '@customTypes/client'
import { ModalProps } from '@customTypes/page'
import { useIsMobile } from '@hooks/mobile'
import { Button, LoadingOverlay, Modal, Text } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isLoadingMutation, isLoadingQuery } from '@utils/network'
import { useForm, zodResolver } from '@mantine/form'
import queryClient from '@api/datasource/query'
import { notifications } from '@mantine/notifications'
import Input from '@components/shared/input'
import ConfirmationModal from '@components/shared/confirmationModal'
import Cont from '@components/shared/cont'

const INITIAL: CreateStatusClientType = { name: '', color: '#FFFFFF', order: 1 }

const StatusForm = (props: ModalProps<StatusClientType>) => {
  const { open, modal, data, size = 'lg', onClose: outerOnClose } = props

  const isMobile = useIsMobile()

  const [confirmExit, setConfirmExit] = React.useState(false)

  const formStatus = useForm<CreateStatusClientType>({
    initialValues: INITIAL,
    validate: zodResolver(CreateStatusClientSchema),
  })

  const statusQuery = useQuery({
    queryKey: [QueryKeys.GET_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getStatus(),
    enabled: Boolean(open),
  })

  const createStatusMutation = useMutation<CreateStatusClientType, Error, CreateStatusClientType>({
    mutationFn: (values) => DataRepo.clientEntityService.createStatus(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_STATUS_KEY], refetchType: 'all' })

      formStatus.resetDirty()
      formStatus.reset()
      outerOnClose?.()

      notifications.show({ message: 'Estado creado correctamente', title: 'Éxito', color: 'green' })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al crear el estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const updateStatusMutation = useMutation<void, Error, StatusClientType>({
    mutationFn: (values) => DataRepo.clientEntityService.updateStatus(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_STATUS_KEY], refetchType: 'all' })

      formStatus.resetDirty()
      formStatus.reset()
      outerOnClose?.()

      notifications.show({
        message: 'Estado actualizado correctamente',
        title: 'Éxito',
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al actualizar el estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  React.useEffect(() => {
    if (open && data) {
      formStatus.setValues(data)
    } else if (!open) {
      formStatus.setValues(INITIAL)
    }
    formStatus.resetDirty()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data])

  const isLoading = isLoadingMutation(createStatusMutation, updateStatusMutation)

  const isLoadingStatus = isLoadingQuery(statusQuery)

  const content = (
    <form
      onSubmit={formStatus.onSubmit((values) => {
        const existingStatus = statusQuery.data?.some((status) => status.name === values.name)
        const isSameStatus = data && data.name === values.name
        if (existingStatus && !isSameStatus) {
          notifications.show({
            message: 'Ya existe un estado con ese nombre',
            title: 'Error',
            color: 'red',
          })
          return
        }

        if (data) {
          updateStatusMutation.mutate(values as StatusClientType)
        } else {
          createStatusMutation.mutate({ ...values, order: (statusQuery.data?.length || 1) + 1 })
        }
      })}
    >
      <div
        className="cd-flex cd-flex-col cd-gap-y-[1rem]"
        // loaderProps={{ type: 'dots' }}
        // visible={isLoadingStatus}
      >
        <LoadingOverlay
          overlayProps={{ radius: 'sm', blur: 2 }}
          visible={isLoadingStatus}
          zIndex={1000}
        />
        <Input
          capitalize
          label="Nombre"
          placeholder="Ingrese el nombre"
          typeInput="text"
          {...formStatus.getInputProps('name')}
          onBlur={() => formStatus.validateField('name')}
        />

        <Input
          label="Color"
          typeInput="color"
          withEyeDropper={false}
          {...formStatus.getInputProps('color')}
          onBlur={() => formStatus.validateField('color')}
        />

        <Button
          className="cd-mt-[1rem]"
          disabled={!formStatus.isValid()}
          loaderProps={{ type: 'dots' }}
          loading={isLoading}
          type="submit"
        >
          {data ? 'Actualizar Estado' : 'Crear Estado'}
        </Button>
      </div>
    </form>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={data ? 'Editar Estado' : 'Crear Estado'}
        withCloseButton={modal}
        onClose={() => {
          if (formStatus.isDirty()) {
            setConfirmExit(true)
          } else {
            onClose()
          }
        }}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return <Cont>{content}</Cont>

  function onClose() {
    outerOnClose?.()
    setConfirmExit(false)
    formStatus.reset()
  }
}

export default StatusForm
